import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { applySeo } from './applySeo';
import type { SeoInput } from './types';

export type SeoProps = Omit<SeoInput, 'path'> & {
  /** Override the canonical path. Defaults to the current route's pathname. */
  path?: string;
};

/**
 * Declares the document head for the page that renders it. Render one per
 * route, near the top of the page component:
 *
 * ```tsx
 * <Seo title="About" description={aboutText} jsonLd={[aboutPageSchema(aboutText)]} />
 * ```
 *
 * Renders nothing. The head is written in an effect, which means it lands after
 * paint — fine for Google (it re-reads the DOM after JS runs) but *not* for
 * social scrapers, which never execute JS. Those are served the real tags by
 * the edge Function in `functions/articles/[slug].ts`.
 */
export function Seo(props: SeoProps) {
  const { pathname } = useLocation();
  const path = props.path ?? pathname;

  // Serializing gives the effect a stable primitive dependency, so re-renders
  // that don't change the metadata don't churn the head (and don't rebuild the
  // JSON-LD scripts on every keystroke elsewhere in the tree).
  const serialized = JSON.stringify({ ...props, path } satisfies SeoInput);

  useEffect(() => {
    applySeo(JSON.parse(serialized) as SeoInput);
  }, [serialized]);

  return null;
}
